import React, { FC, memo, useCallback, useState, useMemo, useEffect } from 'react';
import _ from 'lodash';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { Button } from 'shared/ui/Button';
import NoResultsImage from 'shared/assets/images/NoResultsImage.svg';
import { MyBooking } from 'widgets/MyBooking';
import {
  getLoggedUserData,
  useGetUserInvoicesQuery,
  useGetUserReservationsQuery,
  UserAccountType,
  ProfileVerificationStatus,
} from 'entities/User';
import { useAppDispatch, useAppSelector } from 'app/config/storeConfig/hooks';
import { NoResults } from 'shared/ui/NoResults';
import { AppRoutes } from 'app/config/routerConfig/types';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetContractsWithBookingInfoQuery, useGetUserBookingsQuery } from 'entities/Contract';
import { ContractDetailsDrawer, contractDetailsDrawerActions } from 'widgets/ContractDetailsDrawer';
import { PageLoader } from 'features/PageLoader';
import { CopyTooltip } from 'shared/ui/CopyTooltip';
import { TakeUpReservationModal } from 'widgets/TakeUpReservationModal';
import { CancelReservationModal } from 'features/CancelReservationModal';
import { ReservationDetailsDrawer } from 'widgets/ReservationDetailsDrawer';
import { VerificationReminderModal } from 'features/VerificationReminderModal/ui/VerificationReminderModal';
import { UploadDocumentsModal } from 'widgets/AccountDetailsForm/ui/UploadDocumentsModal';
import { UnpaidBillsNotification } from 'pages/MyBoxesPage/ui/UnpaidBillsNotification';
import { InvoiceStatus } from 'entities/Invoice';

export const MyBookingsPage: FC = memo(() => {
  const { t } = useAppTranslation(['contracts', 'common', 'payment']);

  const { warehouseId } = useParams();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const loggedUserData = useAppSelector(getLoggedUserData);

  const [isUploadDocsPopupOpened, setUploadDocsPopupOpened] = useState(false);
  const [isUploadDocsModalOpened, setUploadDocsModalOpened] = useState(false);

  const accountType = loggedUserData?.accountType as UserAccountType;
  const verificationStatus = loggedUserData?.profileVerificationStatus || null;

  const { data: reservations, isLoading: reservationsLoading } = useGetUserReservationsQuery(
    { userId: loggedUserData?.userId, active: true },
    {
      skip: !loggedUserData?.userId,
    },
  );

  const { data: bookings, isLoading: bookingsLoading } = useGetUserBookingsQuery(loggedUserData?.userId, {
    skip: !loggedUserData?.userId,
  });

  const { data: contractsData, isLoading: bookingContractsLoading } = useGetContractsWithBookingInfoQuery(loggedUserData?.userId, {
    skip: !loggedUserData?.userId,
  });

  const { data: unpaidInvoices, isLoading: unpaidInvoicesLoading } = useGetUserInvoicesQuery(
    {
      userId: loggedUserData?.userId,
      status: InvoiceStatus.OVERDUE,
      orderBy: 'dueDate:ASC',
      limit: 3,
    },
    {
      skip: !loggedUserData?.userId,
    },
  );

  const goToBookingPage = (): void => {
    navigate(AppRoutes.BOOKING);
  };

  const openContractDetails = useCallback(
    (contractId: string): void => {
      dispatch(contractDetailsDrawerActions.setOpenContractDetails(contractId));
    },
    [dispatch],
  );

  const filteredMyBookings = useMemo(() => {
    const filteredBookingsByWarehouseId = bookings?.filter((booking) => booking.warehouseId === warehouseId);

    return filteredBookingsByWarehouseId;
  }, [bookings, warehouseId]);

  const filteredMyReservations = useMemo(() => {
    const filteredReservationsByWarehouseId = reservations?.filter((reservation) => reservation.warehouse.warehouseId === warehouseId);

    const grouped = _.groupBy(filteredReservationsByWarehouseId, (reservation) => reservation.warehouse.warehouseId);

    return Object.values(grouped).map((reservations) => {
      const reservation = reservations[0];
      return {
        ...reservation,
        boxes: _.flatMap(reservations, (reservation) => reservation.boxes),
      };
    });
  }, [reservations, warehouseId]);

  const handleOpenUploadDocsModal = useCallback(() => {
    setUploadDocsPopupOpened(false);
    setUploadDocsModalOpened(true);
  }, []);

  const handleCloseUploadDocsModal = useCallback(() => {
    setUploadDocsModalOpened(false);
  }, []);

  useEffect(() => {
    if (verificationStatus === null || verificationStatus === ProfileVerificationStatus.REJECTED) {
      setUploadDocsPopupOpened(true);
    }
  }, [verificationStatus, warehouseId]);

  if (reservationsLoading || bookingsLoading || unpaidInvoicesLoading || bookingContractsLoading) {
    return <PageLoader />;
  }

  return (
    <div className="max-w-[1200px] mx-auto">
      <ContractDetailsDrawer />
      <CancelReservationModal />
      <TakeUpReservationModal />
      <ReservationDetailsDrawer />
      {unpaidInvoices?.content && !_.isEmpty(unpaidInvoices.content) && <UnpaidBillsNotification invoices={unpaidInvoices.content} />}
      {!_.isEmpty(filteredMyReservations) || !_.isEmpty(filteredMyBookings) ? (
        <div className="flex gap-1 px-4 desktop:pt-7 flex-col gap-6">
          {filteredMyReservations?.map((reservation) => {
            const { warehouse, boxes } = reservation;

            return (
              <MyBooking
                warehouse={warehouse}
                boxes={boxes}
                reservation={reservation}
                type="reservation"
                onOpenContractDetails={openContractDetails}
                key={warehouse.warehouseId}
              />
            );
          })}

          {/* {!_.isEmpty(bookings) && (
            <div className="mt-2">
              <InfoTag>
                {t('You can extend the rental of the box or notify Us about the end of the rental by going to Contract details')}
              </InfoTag>
            </div>
          )} */}

          {filteredMyBookings?.map((warehouse) => {
            return (
              <MyBooking
                warehouse={warehouse}
                boxes={warehouse.userBoxes}
                contractsData={contractsData}
                type="contract"
                onOpenContractDetails={openContractDetails}
                key={warehouse.warehouseId}
              />
            );
          })}
        </div>
      ) : (
        <NoResults
          image={NoResultsImage}
          title={t('Looks like it`s empty here')}
          description={t('Oops, you don`t have reservations or rental boxes. Go to the booking page if you would like to rent a box')}
          actions={
            <Button containerClassName="w-full desktop:w-auto" size="medium" onClick={goToBookingPage}>
              {t('Go to booking')}
            </Button>
          }
        />
      )}

      <VerificationReminderModal
        isOpen={isUploadDocsPopupOpened}
        onClose={() => {
          setUploadDocsPopupOpened(false);
        }}
        onUploadClick={handleOpenUploadDocsModal}
        status={verificationStatus}
      />

      {loggedUserData?.userId && (
        <UploadDocumentsModal
          isOpen={isUploadDocsModalOpened}
          onClose={handleCloseUploadDocsModal}
          accountType={accountType}
          userId={loggedUserData?.userId}
        />
      )}
    </div>
  );
});
